// src/AppLayout.js
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useFriends } from "./FriendsContext";
import { useXP } from "./XPContext";
import { sanitizeShortText } from "./sanitize";
import LevelProgress from "./LevelProgress";
import ThemeToggle from "./ThemeToggle";
import Confetti from "./Confetti";
import SyncStatus from "./SyncStatus";
import { usePWAInstall } from "./usePWAInstall";
import { createPortal } from "react-dom";
import LanguageSelector from "./LanguageSelector";
import { JournalNameProvider } from "./JournalNameContext";
import GlobalSearch from "./GlobalSearch";
import ExportJournalButtons from "./ExportJournalButtons";
import JournalReminderControls from "./JournalReminderControls";
import { useJournalReminder } from "./useJournalReminder";
import useIsMobile, { useIsTablet } from "./useIsMobile";

const AppLayout = ({ children, isDarkMode, toggleTheme }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const location = useLocation();
  const navigate = useNavigate();
  const { levelUpTrigger } = useXP();
  const { receivedRequests, userProfile, updateUserProfile } = useFriends();
  const { signOut, user } = useAuth();
  const { canInstall, isIOS, install } = usePWAInstall();
  useJournalReminder();
  const menuRef = useRef(null);
  const hasFriendNotifications = receivedRequests && receivedRequests.length > 0;

  const [journalName, setJournalName] = useState("ihkeely");
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [newJournalName, setNewJournalName] = useState("");
  const [renameError, setRenameError] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editUsername, setEditUsername] = useState("");
  const [editDisplayName, setEditDisplayName] = useState("");
  const [editProfileError, setEditProfileError] = useState("");

  const isJournalPage = location.pathname === "/journal";
  const journalViewParam = new URLSearchParams(location.search).get("view");
  const journalView = isJournalPage
    ? (["dashboard", "insights", "moodMatrix", "journal"].includes(journalViewParam)
      ? journalViewParam
      : "journal")
    : null;
  const hideNavForMobileJournal = isMobile && isJournalPage;
  const isTabletSketch = isTablet && isJournalPage && journalViewParam === "sketch";

  useEffect(() => {
    if (user) {
      const savedName = localStorage.getItem(`journalName_${user.id}`);
      if (savedName && savedName !== "Life Journal") setJournalName(savedName);
      else setJournalName("ihkeely");
    }
  }, [user]);

  useEffect(() => {
    document.body.classList.add("main-page-design");
    return () => document.body.classList.remove("main-page-design");
  }, []);

  useEffect(() => {
    document.body.classList.toggle("is-tablet", isTablet);
    return () => document.body.classList.remove("is-tablet");
  }, [isTablet]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (isTablet) return;
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [menuOpen, isTablet]);

  const handleSignOut = async () => {
    setMenuOpen(false);
    await signOut();
    navigate('/');
  };

  const handleRenameClick = () => {
    setNewJournalName(journalName);
    setShowRenameModal(true);
    setRenameError("");
  };

  const handleSaveRename = () => {
    const sanitized = sanitizeShortText(newJournalName, 50);
    if (!sanitized) {
      setRenameError(t("journalNameCannotBeEmpty"));
      return;
    }
    setJournalName(sanitized);
    if (user) localStorage.setItem(`journalName_${user.id}`, sanitized);
    setShowRenameModal(false);
    setNewJournalName("");
    setRenameError("");
  };

  const handleCancelRename = () => {
    setShowRenameModal(false);
    setNewJournalName("");
    setRenameError("");
  };

  const handleRenameKeyPress = (e) => {
    if (e.key === "Enter") handleSaveRename();
    else if (e.key === "Escape") handleCancelRename();
  };

  const closeMenu = () => setMenuOpen(false);

  const openEditProfile = () => {
    setEditUsername(userProfile?.username || "");
    setEditDisplayName(userProfile?.display_name || "");
    setEditProfileError("");
    setShowEditProfile(true);
    setMenuOpen(false);
  };

  const saveEditProfile = async () => {
    if (!editUsername.trim()) {
      setEditProfileError(t("usernameEmpty"));
      return;
    }
    const { error } = await updateUserProfile({
      username: editUsername.trim().toLowerCase(),
      display_name: editDisplayName.trim() || editUsername.trim(),
    });
    if (error) {
      setEditProfileError(error.code === "23505" ? t("usernameTaken") : t("profileUpdateFailed"));
    } else {
      setShowEditProfile(false);
    }
  };

  const layoutContent = (
    <div className={`app-wrapper main-page-wrap${isTablet && menuOpen ? " tablet-menu-open" : ""}${isTabletSketch ? " tablet-sketch-mode" : ""}`}>
      <Confetti trigger={levelUpTrigger} isMobile={isMobile} />
      <header className="main-page-header glass">
        <div className="main-page-header-left">
          {isTablet && (
            <button
              type="button"
              className="main-page-menu-btn tablet-menu-btn"
              onClick={() => setMenuOpen((o) => !o)}
              title={t("menu")}
              aria-label={t("openMenu")}
              aria-expanded={menuOpen}
            >
              <span className="material-symbols-outlined">menu</span>
            </button>
          )}
          <h1 className="main-page-title">
            <button
              type="button"
              className="main-page-title-link"
              onClick={() => navigate("/journal")}
            >
              {journalName}
            </button>
          </h1>
          <LevelProgress />
        </div>
        <div className="main-page-header-right">
          <SyncStatus />
          {!isMobile && !isTablet && <GlobalSearch />}
          <button
            type="button"
            className="main-page-friends-btn"
            onClick={() => navigate(isTablet ? "/journal?view=friends" : "/friends")}
            title={t("friends")}
            style={{ position: "relative" }}
          >
            <span className="material-symbols-outlined">group</span>
            <span className="main-page-friends-label">{t("friends")}</span>
            {hasFriendNotifications && (
              <span className="notification-dot" style={{ position: "absolute", top: "4px", right: "4px" }} />
            )}
          </button>
          <div className="main-page-user-email">{user?.email}</div>
          <div className="main-page-avatar">
            <div className="main-page-avatar-inner">
              {user?.user_metadata?.avatar_url ? (
                <img src={user.user_metadata.avatar_url} alt="" />
              ) : (
                <span style={{ fontSize: "1rem", fontWeight: 700 }}>
                  {user?.email?.slice(0, 1).toUpperCase() || "?"}
                </span>
              )}
            </div>
          </div>
          <div ref={menuRef} style={{ position: "relative" }}>
            {!isTablet && (
              <button
                type="button"
                className="main-page-menu-btn"
                onClick={() => setMenuOpen((o) => !o)}
                title={t("menu")}
                aria-label={t("openMenu")}
                aria-expanded={menuOpen}
              >
                <span className="material-symbols-outlined">menu</span>
              </button>
            )}
            {!isTablet && menuOpen && (
              <div className="hamburger-dropdown">
                <div className="hamburger-section">
                  <button
                    type="button"
                    className={`hamburger-item ${journalView === "journal" ? "active" : ""}`}
                    onClick={() => { navigate("/journal"); closeMenu(); }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
                      <path d="M7 8h10M7 12h10M7 16h6" strokeLinecap="round" />
                    </svg>
                    {t("journalTab")}
                  </button>
                  <button
                    type="button"
                    className={`hamburger-item ${journalView === "dashboard" ? "active" : ""}`}
                    onClick={() => { navigate("/journal?view=dashboard"); closeMenu(); }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M3 3v18h18" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M18 9v4M12 3v18M7 12v9" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {t("dashboard")}
                  </button>
                  <button
                    type="button"
                    className={`hamburger-item ${location.pathname === "/favourites" ? "active" : ""}`}
                    onClick={() => { navigate("/favourites"); closeMenu(); }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                    {t("favouriteMoments")}
                  </button>
                  <button
                    type="button"
                    className={`hamburger-item ${location.pathname === "/sketches" ? "active" : ""}`}
                    onClick={() => { navigate("/sketches"); closeMenu(); }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M12 19l7-7 3 3-7 7-3-3z" />
                      <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {t("sketchGallery")}
                  </button>
                  <button
                    type="button"
                    className={`hamburger-item ${location.pathname === "/entries" ? "active" : ""}`}
                    onClick={() => { navigate("/entries"); closeMenu(); }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
                      <path d="M7 8h10M7 12h10M7 16h6" strokeLinecap="round" />
                    </svg>
                    {t("allJournalEntries")}
                  </button>
                  <button
                    type="button"
                    className={`hamburger-item ${journalView === "insights" ? "active" : ""}`}
                    onClick={() => { navigate("/journal?view=insights"); closeMenu(); }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 21H4.6c-.56 0-.84 0-1.054-.109a1 1 0 0 1-.437-.437C3 20.24 3 19.96 3 19.4V3" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M7 14l4-4 4 4 6-6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {t("insightsTrends")}
                  </button>
                  <button
                    type="button"
                    className={`hamburger-item ${journalView === "moodMatrix" ? "active" : ""}`}
                    onClick={() => { navigate("/journal?view=moodMatrix"); closeMenu(); }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="3" width="7" height="7" rx="1.5" />
                      <rect x="14" y="3" width="7" height="7" rx="1.5" />
                      <rect x="3" y="14" width="7" height="7" rx="1.5" />
                      <rect x="14" y="14" width="7" height="7" rx="1.5" />
                    </svg>
                    {t("moodMatrixTitle")}
                  </button>
                  <button
                    type="button"
                    className={`hamburger-item ${location.pathname === "/map" ? "active" : ""}`}
                    onClick={() => { navigate("/map"); closeMenu(); }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M3.5 9h17M3.5 15h17M12 3c2.5 2.6 3.75 5.6 3.75 9S14.5 18.4 12 21c-2.5-2.6-3.75-5.6-3.75-9S9.5 5.6 12 3z" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {t("journalMap")}
                  </button>
                </div>
                <div className="hamburger-divider" />
                <div className="hamburger-section">
                  <span className="hamburger-section-title">{t("settings")}</span>
                  {canInstall && (
                    <button
                      type="button"
                      className="hamburger-item hamburger-install"
                      onClick={isIOS ? undefined : install}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      <span className="hamburger-install-text">
                        <span>{t("installApp")}</span>
                        <span className="hamburger-install-hint">
                          {isIOS ? t("installAppIOS") : t("installAppDesc")}
                        </span>
                      </span>
                    </button>
                  )}
                  <div className="hamburger-row">
                    <span className="hamburger-label">{t("theme")}</span>
                    <ThemeToggle isDarkMode={isDarkMode} onToggle={toggleTheme} />
                  </div>
                  <div className="hamburger-row">
                    <span className="hamburger-label">{t("language")}</span>
                    <LanguageSelector className="hamburger-lang-select" />
                  </div>
                  <button
                    type="button"
                    className="hamburger-item"
                    onClick={openEditProfile}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    {t("editProfile")}
                  </button>
                  <JournalReminderControls />
                  <span className="hamburger-section-title">{t("exportData")}</span>
                  <ExportJournalButtons />
                  <button
                    type="button"
                    className="hamburger-item hamburger-signout"
                    onClick={handleSignOut}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    {t("signOut")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
      {isTablet && menuOpen && createPortal(
        <>
          <div className="tablet-drawer-backdrop" onClick={closeMenu} aria-hidden="true" />
          <nav className="hamburger-dropdown is-tablet-drawer" aria-label={t("menu")}>
            <div className="hamburger-section">
              <div className="tablet-drawer-heading">
                <span>{t("menu")}</span>
                <button
                  type="button"
                  className="tablet-drawer-close"
                  onClick={closeMenu}
                  aria-label={t("close")}
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
              <button
                type="button"
                className={`hamburger-item ${location.pathname === "/journal" && !["trackers", "settings", "sketch", "friends", "dashboard", "insights", "moodMatrix", "map"].includes(journalViewParam || "") ? "active" : ""}`}
                onClick={() => { navigate("/journal"); closeMenu(); }}
              >
                <span className="material-symbols-outlined">book</span>
                {t("journalAndHabits")}
              </button>
              <button
                type="button"
                className={`hamburger-item ${journalViewParam === "trackers" ? "active" : ""}`}
                onClick={() => { navigate("/journal?view=trackers"); closeMenu(); }}
              >
                <span className="material-symbols-outlined">monitoring</span>
                {t("trackersTab")}
              </button>
              <button
                type="button"
                className={`hamburger-item ${journalViewParam === "sketch" ? "active" : ""}`}
                onClick={() => { navigate("/journal?view=sketch"); closeMenu(); }}
              >
                <span className="material-symbols-outlined">brush</span>
                {t("sketchDay")}
              </button>
              <button
                type="button"
                className={`hamburger-item ${journalViewParam === "friends" || location.pathname === "/friends" ? "active" : ""}`}
                onClick={() => { navigate("/journal?view=friends"); closeMenu(); }}
              >
                <span className="material-symbols-outlined">group</span>
                {t("friends")}
              </button>
              <div className="hamburger-divider" />
              <button
                type="button"
                className={`hamburger-item ${journalViewParam === "dashboard" ? "active" : ""}`}
                onClick={() => { navigate("/journal?view=dashboard"); closeMenu(); }}
              >
                <span className="material-symbols-outlined">dashboard</span>
                {t("dashboard")}
              </button>
              <button
                type="button"
                className={`hamburger-item ${journalViewParam === "insights" ? "active" : ""}`}
                onClick={() => { navigate("/journal?view=insights"); closeMenu(); }}
              >
                <span className="material-symbols-outlined">insights</span>
                {t("insightsTrends")}
              </button>
              <button
                type="button"
                className={`hamburger-item ${journalViewParam === "moodMatrix" ? "active" : ""}`}
                onClick={() => { navigate("/journal?view=moodMatrix"); closeMenu(); }}
              >
                <span className="material-symbols-outlined">grid_view</span>
                {t("moodMatrixTitle")}
              </button>
              <button
                type="button"
                className={`hamburger-item ${journalViewParam === "map" ? "active" : ""}`}
                onClick={() => { navigate("/journal?view=map"); closeMenu(); }}
              >
                <span className="material-symbols-outlined">public</span>
                {t("journalMap")}
              </button>
              <div className="hamburger-divider" />
              <div className="hamburger-row">
                <span className="hamburger-label">{t("theme")}</span>
                <ThemeToggle isDarkMode={isDarkMode} onToggle={toggleTheme} />
              </div>
              <button
                type="button"
                className={`hamburger-item ${journalViewParam === "settings" ? "active" : ""}`}
                onClick={() => {
                  navigate("/journal?view=settings", {
                    replace: journalViewParam === "settings",
                    state: { settingsReset: Date.now() },
                  });
                  closeMenu();
                }}
              >
                <span className="material-symbols-outlined">settings</span>
                {t("settings")}
              </button>
            </div>
          </nav>
        </>,
        document.body
      )}
      <main className="main-page-content main-page-content-layout">
        {children}
      </main>
    </div>
  );

  const providerValue = { journalName, openRenameModal: handleRenameClick };

  const renameModal = showRenameModal &&
    createPortal(
      <div className="year-modal-overlay year-modal-overlay-centered" onClick={handleCancelRename}>
        <div className="year-modal-content rename-journal-modal" onClick={(e) => e.stopPropagation()}>
          <h3 className="year-modal-title">{t("renameJournal")}</h3>
          <input
            type="text"
            value={newJournalName}
            onChange={(e) => setNewJournalName(e.target.value)}
            onKeyDown={handleRenameKeyPress}
            placeholder={t("enterJournalName")}
            className="year-modal-input"
            maxLength="50"
            autoFocus
          />
          <div className="character-count">
            {newJournalName.length}/50 {t("characters")}
          </div>
          {renameError && <div className="year-modal-error">{renameError}</div>}
          <div className="year-modal-actions">
            <button onClick={handleCancelRename} className="year-modal-btn cancel-btn">
              {t("cancel")}
            </button>
            <button onClick={handleSaveRename} className="year-modal-btn save-btn">
              {t("save")}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );

  const editProfileModal = showEditProfile &&
    createPortal(
      <div className="year-modal-overlay year-modal-overlay-centered" onClick={() => setShowEditProfile(false)}>
        <div className="year-modal-content" onClick={(e) => e.stopPropagation()}>
          <h3 className="year-modal-title">{t("editProfile")}</h3>
          <div className="friends-social-profile-edit">
            <div className="profile-edit-fields">
              <div className="profile-field">
                <label>{t("usernameLabel")}</label>
                <input
                  type="text"
                  value={editUsername}
                  onChange={(e) =>
                    setEditUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))
                  }
                  placeholder={t("usernamePlaceholder")}
                  maxLength={20}
                />
              </div>
              <div className="profile-field">
                <label>{t("displayName")}</label>
                <input
                  type="text"
                  value={editDisplayName}
                  onChange={(e) => setEditDisplayName(e.target.value)}
                  placeholder={t("displayName")}
                  maxLength={30}
                />
              </div>
            </div>
            {editProfileError && <div className="profile-error">{editProfileError}</div>}
            <div className="profile-edit-actions">
              <button type="button" className="cancel-btn" onClick={() => setShowEditProfile(false)}>
                {t("cancel")}
              </button>
              <button type="button" className="save-btn" onClick={saveEditProfile}>
                {t("save")}
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );

  if (hideNavForMobileJournal) {
    return (
      <JournalNameProvider value={providerValue}>
        {children}
        {renameModal}
        {editProfileModal}
      </JournalNameProvider>
    );
  }

  return (
    <JournalNameProvider value={providerValue}>
      {layoutContent}
      {renameModal}
      {editProfileModal}
    </JournalNameProvider>
  );
};

export default AppLayout;
